
// Implementación actualizada del componente AddVaccine
import { AppBackground, Button, Drop, Input, Text, ResponsiveLogo, BottomActionContainer } from '@ui/components'
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ReactComponent as PawIcon } from '@/assets/icons/paw.svg'
import { ReactComponent as PawIconBlack } from '@/assets/icons/paw_color.svg'
import { ReactComponent as XSimpleIcon } from '@/assets/icons/x_simple_grey.svg'
import {
  DatePickerWrapper,
  InputContainer,
  InputWrapper,
  ModalContainer,
  ModalHeader,
  Headline5,
  DisabledButton,
  ButtonContent,
  DisabledButtonText,
  ErrorText,
} from '@/aena/styles'
import { colors } from '@/aena/styles'
import { CamaraCard, ImageTypeSelector } from '@/components/cameracard'
import DatePicker from 'react-datepicker'
import { getDatePickerLocale } from '@/utils/datepickerLocale'
import { loadActualVaccineUrl, useAppDispatch, useAppSelector } from '@front/store'
import { FormikValues } from 'formik'
import { usePetVaccines } from '@front/logic'
import { View, TouchableOpacity, Modal } from 'react-native'
import { HeaderWithBackAena } from '@/components/headerwithback'
import { t } from 'i18next'
import { ModalWrapper } from '@/aena/styles'
import { SwitchSelector } from '@/components/switchselector'

const option1 = 'MANDATORY'
const option2 = 'OPTIONAL'

const AddVaccine: React.FC = () => {

  const vaccines = useAppSelector((state) => state.petDocumentation.vaccines)
  const mandatoryVaccines = useAppSelector((state) => state.petDocumentation.mandatoryVaccines)
  const actualVaccineUrl = useAppSelector((state) => state.petDocumentation.actualVaccineUrl)
  const [vaccineType, setVaccineType] = useState<'MANDATORY' | 'OPTIONAL'>('MANDATORY')
  const [showModal, setShowModal] = useState(false)
  const [date, setDate] = useState<Date | undefined>(undefined)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const onNextPress = (values: FormikValues) => {
    navigate(-1)
  }

  const { setFieldValue, values, handleBlur, handleChange, errors, handleSubmit, getVaccines } =
    usePetVaccines(onNextPress)

  useEffect(() => {
    console.debug('ERRORS', errors)
  }, [errors])

  useEffect(() => {
    if (vaccines.length <= 0) {
      getVaccines()
    }
  }, [vaccines])

  // Resetear la selección de vacuna cuando cambia el tipo
  useEffect(() => {
    setFieldValue('vaccineGuid', '')
  }, [vaccineType, setFieldValue])

  const handleSubmitForm = async () => {
    // await setFieldValue('vaccineDate', date)
    if (actualVaccineUrl) {
      handleSubmit()
    }
  }

  const handleDateChange = (date: Date | null) => {
    // console.log('DATE', date?.toISOString())
    if (date) {
      setDate(date)
      setFieldValue('vaccineDate', date)
    }
  }

  const onGalleryPress = (file: File, preview: string) => {
    setFieldValue('vaccineUrl', file)
    dispatch(loadActualVaccineUrl(preview))
  }

  const handlePreviewPress = () => {
    setShowModal(!showModal)
  }

  const handleVaccineTypeChange = (type: 'MANDATORY' | 'OPTIONAL') => {
    setVaccineType(type)
  }

  // Obtener las vacunas filtradas según el tipo seleccionado
  const getFilteredVaccines = () => {
    return vaccineType === 'MANDATORY' 
      ? mandatoryVaccines 
      : vaccines.filter(v => v.type !== "MANDATORY")
  }

  const filteredVaccines = getFilteredVaccines()

  const canSubmit = useMemo(() => {
    return values.vaccineUrl && values.vaccineGuid && values.vaccineLote && date
  }, [values,date])
    
  return (
    <AppBackground isSafe>
      <View style={{ maxWidth: 800, width: '100%', alignSelf: 'center' }}>
        <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', padding: 16, gap: 16 }}>
          <ResponsiveLogo
            breakpoint={820}
            hysteresis={20}
            large={{ width: 680, height: 96 }}
            small={{ width: 360, height: 48 }}
          />
        </View>
        <HeaderWithBackAena title={t('common.addvaccine')} />
        <BottomActionContainer>

          <View style={{flex:1}}>
            {/* Selector de tipo de vacuna */}
            <SwitchSelector
              option1={option1}
              option2={option2}
              tOption1={t('common.addmandatory') || 'Obligatorias'}
              tOption2={t('common.addoptional') || 'Opcionales'}
              selectedType={vaccineType}
              onTypeChange={handleVaccineTypeChange}
            />
            <InputWrapper>
            <Headline5>{t('common.photo')} *</Headline5>
            <InputContainer>
              <CamaraCard
                title={t('common.choosephoto')}
                subtitle={t('common.uploadvaccinationcard')}
                onGalleryPress={onGalleryPress}
                url={actualVaccineUrl}
                type={ImageTypeSelector.VACCINE}
              />
              {errors.vaccineUrl && (
                <ErrorText>
                  {errors.vaccineUrl.toString()}
                </ErrorText>
              )}
            </InputContainer>
            </InputWrapper>
            <InputWrapper>
              <Headline5>{t('vaccines.vaccine')} *</Headline5>
            <InputContainer>
              <Drop
                buttonText={t('common.continue')}
                placeholder={t('common.selectvaccine')}
                icon={<PawIconBlack />}
                data={filteredVaccines.map((vaccine) => ({
                  title: t(`vaccines.${vaccine.tname}`),
                  id: vaccine.guid,
                  data: { ...vaccine },
                }))}
                active={filteredVaccines.findIndex(
                  (obj) => obj.guid === values.vaccineGuid,
                )}
                selectedItem={filteredVaccines.findIndex(
                  (obj) => obj.guid === values.vaccineGuid,
                )}
                onPress={(item) => {
                  setFieldValue('vaccineGuid', item.id)
                }}
                allowIconAndText
                variant="outlined"
                color="aena"
              />
              {!!errors.vaccineGuid ? (
                <ErrorText>
                  {errors.vaccineGuid.toString()}
                </ErrorText>
              ) : (
                <></>
              )}
            </InputContainer>
            </InputWrapper>

            <InputWrapper>
              <Headline5>{t('common.lotreference')} *</Headline5>
              <InputContainer>
                <Input
                  inputProps={{
                    value: values.vaccineLote,
                    onChangeText: handleChange('vaccineLote'),
                    onBlur: handleBlur('vaccineLote'),
                    placeholder: t('common.lot'),
                  }}
                />
                {errors.vaccineLote && (
                  <ErrorText>
                    {errors.vaccineLote.toString()}
                  </ErrorText>
                )}
              </InputContainer>
            </InputWrapper>

            <InputWrapper >
              <Headline5>{t('common.vaccinationappointment')} *</Headline5>
              <InputContainer >
                <DatePickerWrapper>
                  <DatePicker
                    selected={date}
                    date={date}
                    onChange={handleDateChange}
                    // onBlur={handleBlur('vaccineDate')}
                    dateFormat="dd/MM/yyyy"
                    placeholderText={t('common.selectadate')}
                    className="datepicker-styled"
                    popperPlacement="bottom-start"
                    dropdownMode="select"
                    maxDate={new Date()}
                    locale={getDatePickerLocale()}
                    showMonthDropdown
                    showYearDropdown
                  />
                </DatePickerWrapper>
                {errors.vaccineDate && (
                  <ErrorText>
                    {errors.vaccineDate.toString()}
                  </ErrorText>
                )}                
              </InputContainer>
            </InputWrapper>

          </View>
          {canSubmit ? (
              <Button
                title={t('common.add')}
                icon={<PawIcon width={24} height={24} />}
                size="block"
                textType="cta_medium"
                borderRadius={5}
                buttonWrapperStyle={{ backgroundColor: colors.aenaVerde }}
                textColor="white"
                allowIconAndText
                color="primary"
                onPress={handleSubmitForm}
              />
            ) : (
              <DisabledButton disabled>
                <ButtonContent>
                  <PawIcon width={24} height={24} fill="#1a2732" />
                  <DisabledButtonText>{t('common.add')}</DisabledButtonText>
                </ButtonContent>
              </DisabledButton>
            )
          }          
          {/* <Button
            title={t('common.add')}
            buttonWrapperStyle={{ backgroundColor: colors.aenaVerde }}
            icon={<PawIcon width={24} height={24} />}
            size="block"
            textType="cta_medium"
            borderRadius={5}
            textColor="background0"
            allowIconAndText
            color="primary"
            onPress={handleSubmitForm}
          /> */}
        </BottomActionContainer>
        <Modal
          visible={showModal}
          transparent={true}
          onRequestClose={handlePreviewPress}
        >
          <ModalWrapper>  
            <ModalContainer>
              <ModalHeader>
                <Text type="H5">{t('common.preview')}</Text>
                <TouchableOpacity onPress={handlePreviewPress}>
                  <XSimpleIcon width={30} height={30} />
                </TouchableOpacity>
              </ModalHeader>
              <img src={actualVaccineUrl} style={{ width: 300, height: 300 }} />
            </ModalContainer>
          </ModalWrapper>
        </Modal>        
      </View>
    </AppBackground>
  )
}

export default AddVaccine

// export { VaccineTypeSelector }
